import React, { useState } from 'react';
import classNames from 'classnames';

import { EnergyRating } from '@JohnLewisPartnership/jl-design-system/components/energy-rating/EnergyRating';
import Price from '@JohnLewisPartnership/jl-design-system/components/price/Price';
import { SocialProofMessage } from '@JohnLewisPartnership/jl-design-system/components/social-proof-message/SocialProofMessage';
import AdditionalMessages from '@JohnLewisPartnership/jl-design-system/components/product-card/additional-messages/AdditionalMessages';
import Image from '@JohnLewisPartnership/jl-design-system/components/product-card/image/Image';
import PreOrder from '@JohnLewisPartnership/jl-design-system/components/product-card/pre-order/PreOrder';
import ProductCardRating from '@JohnLewisPartnership/jl-design-system/components/product-card/rating/ProductCardRating';
import PromoMessages from '@JohnLewisPartnership/jl-design-system/components/product-card/promo-messages/PromoMessages';
import Sponsored from '@JohnLewisPartnership/jl-design-system/components/product-card/sponsored/Sponsored';
import Title from '@JohnLewisPartnership/jl-design-system/components/product-card/title/Title';
import HeroAttributes from '@JohnLewisPartnership/jl-design-system/components/product-card/ALPHA/hero-attributes/HeroAttributes';
import Accordion from '@JohnLewisPartnership/jl-design-system/components/accordion/Accordion';
import Swatches from '@JohnLewisPartnership/jl-design-system/components/product-card/swatches/Swatches';
import getProductUrl from '@JohnLewisPartnership/jl-design-system/utils/product/getProductUrl';
import { isAnydayBrand } from '@JohnLewisPartnership/jl-design-system/utils/brands/brands';
import { shouldShowQuickViewButton } from '@JohnLewisPartnership/jl-design-system/components/product-card/quick-view-drawer/shouldShowQuickViewButton';
import { VisibleOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/VisibleOutlined';
import { IconButton } from '@JohnLewisPartnership/jl-design-system/elements/button/IconButton';
import { useEmailConfirmed } from '@JohnLewisPartnership/jl-design-system/utils/hooks/useEmailConfirmed';
import { CtaButton } from '@JohnLewisPartnership/jl-design-system/components/product-card/cta-button/CtaButton';

import { WishlistOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/WishlistOutlined';

import styles from './ProductCardListView.module.scss';

const ProductCardListView = ({
  product,
  hasAltImageOnHover = false,
  hasEnergySideDrawer = false,
  hasHeroAttributes = false,
  hasProductRating = false,
  hasPromoMessageModal = false,
  imageSettings = {},
  selectedSwatch = {},
  socialProofMessage = null,
  sponsoredTitleEnabled = false,
  showSwatchesNotDisplayedTotal = false,
  handleClick = () => {},
  handleDisplayPromoModal = undefined,
  handleEnergyRatingClick = () => {},
  handleMoreInformationClick = () => {},
  handleQuickAddClick = () => {},
  handleQuickViewClick = () => {},
  handleSwatchClick: handleSwatchClickProp = () => {},
  position = 0,
  children,
}) => {
  const {
    additionalMessages,
    adId,
    alternativeImageUrls,
    availability,
    brand,
    colorSwatches,
    creditMessage,
    defaultSkuId,
    energy,
    heroAttributes,
    id,
    image,
    isMultiSku,
    price,
    promoMessages = [],
    rating,
    title,
    url,
  } = product;

  const buttonSettings = {
    hasEmailMeWhenAvailableButton: true,
    hasMoreInformationButton: product.type === 'productSet',
    hasAddToBasketButton: false,
    hasNotifyMeButton: false,
    hasQuickViewButton: false,
  };

  const { hasEmailMeWhenAvailableButton } = buttonSettings;
  const emailConfirmed = useEmailConfirmed(hasEmailMeWhenAvailableButton, id);

  const [swatchOverride, setSwatchOverride] = useState(null);
  const [isQvIconLoading, setIsQvIconLoading] = useState(false);

  const productUrl = swatchOverride
    ? getProductUrl(title, id, swatchOverride, adId)
    : url || getProductUrl(title, id, selectedSwatch, adId);

  const getOverrideSwatch = (e) => {
    const override = colorSwatches.find(
      ({ color }) =>
        color === e.currentTarget.title ||
        `${color} - Out of stock` === e.currentTarget.title
    );

    return override;
  };

  const isAnyday = isAnydayBrand(brand);

  const handleSelectedSwatchClick = (e) => {
    e.preventDefault();
    const override = getOverrideSwatch(e);
    if (override) {
      setSwatchOverride(override);
    }
  };

  const handleSwatchClick = (e) => {
    handleSelectedSwatchClick(e);

    if (handleSwatchClickProp) {
      handleSwatchClickProp(e);
    }
  };

  const handleEnergyArrowClick = (altText, rating) => {
    handleEnergyRatingClick(
      altText,
      rating,
      energy?.label,
      energy?.informationSheet,
      adId,
      id
    );
  };

  const handlePromoMessagesClick = () => {
    const swatchOrder = (colorSwatches || []).map(({ color }) => color);
    return Promise.resolve(
      handleQuickViewClick(
        id,
        selectedSwatch?.url || getProductUrl(title, id, selectedSwatch),
        swatchOrder,
        swatchOverride?.color,
        adId,
        false,
        'VIEW_OFFERS'
      )
    );
  };

  const handleQuickViewIconClick = (e) => {
    setIsQvIconLoading(true);
    const swatchOrder = (colorSwatches || []).map(({ color }) => color);
    return Promise.resolve(
      handleQuickViewClick(
        id,
        selectedSwatch?.url || getProductUrl(title, id, selectedSwatch),
        swatchOrder,
        swatchOverride?.color,
        adId,
        false,
        'QUICK_VIEW_ICON'
      )
    ).then(() => {
      setIsQvIconLoading(false);
    });
  };

  const showHeroAttributes = hasHeroAttributes && !!heroAttributes;
  const showPromoMessages = promoMessages?.length > 0;
  const showPreOrder =
    availability?.futureAvailability?.availableToOrderFromMessage ||
    availability?.preOrder?.streetDateMessage;
  const showAdditionalMessages = additionalMessages?.length > 0;
  const showSwatches = colorSwatches?.length > 1;

  const showKeySpecifications =
    showHeroAttributes ||
    showPromoMessages ||
    showPreOrder ||
    showAdditionalMessages;

  const showQuickViewIconButton = shouldShowQuickViewButton(product);

  return (
    <article
      className={styles.container}
      data-testid="product-card"
      data-product-id={id}
      data-component="product-card-list-view"
    >
      {/* Image */}
      <div className={styles.imageColumn}>
        {socialProofMessage && (
          <SocialProofMessage message={socialProofMessage} />
        )}
        <div className={styles.imageColumn__imageContainer}>
          <Image
            alternativeImageUrls={alternativeImageUrls}
            handleClick={handleClick}
            hasAltImageOnHover={hasAltImageOnHover}
            image={image}
            title={title}
            url={productUrl}
            className={classNames(styles.imageColumn__image, {
              [styles.imageColumn__image__noPadding]:
                !showKeySpecifications &&
                !showSwatches &&
                !showQuickViewIconButton,
            })}
            multipleSwatches={showSwatches}
            selectedSwatch={swatchOverride || selectedSwatch}
            {...imageSettings}
          />
          {showQuickViewIconButton && (
            <IconButton
              className={classNames(styles.quickViewIcon, styles.visible__3col)}
              IconComponent={VisibleOutlined}
              ariaLabel={`Quick view for ${title}`}
              data-testid="icon-quick-view-button"
              onClick={handleQuickViewIconClick}
              size="small"
              submitting={isQvIconLoading}
              title={`Quick view`}
            />
          )}
        </div>

        {showSwatches && (
          <div className={classNames(styles.swatches, styles.swatches__3col)}>
            <Swatches
              baseUrl={productUrl}
              handleClick={handleSwatchClick}
              id={id}
              selectedSwatch={swatchOverride || selectedSwatch}
              swatches={colorSwatches}
              title={title}
              showNotDisplayedTotal={showSwatchesNotDisplayedTotal}
            />
          </div>
        )}
      </div>

      {/* Title, Sponsored */}
      <div
        className={classNames(styles.middleColumn, {
          [styles.middleColumn__noDetails]: !showHeroAttributes,
        })}
      >
        <a href={productUrl} onClick={handleClick} tabIndex={0}>
          <Title
            brand={isAnyday ? 'Anyday' : brand}
            isBranded={!!brand}
            isUnlimitedLines
            title={title}
            brandPrefix={isAnyday ? 'John Lewis ' : ''}
            isAnyday={isAnyday}
          />
        </a>

        <div
          className={styles.wishlist}
          data-product-id={id}
          data-product-multi-sku={isMultiSku ? 'true' : 'false'}
          data-product-skuid={defaultSkuId}
          data-wishlist-placeholder
        >
          {/* // to check wishlist icon locally */}
          {/* <IconButton
            IconComponent={WishlistOutlined}
            IconTitle="Wishlist Icon"
            size="small"
          /> */}
        </div>

        <Sponsored hasSponsoredTitle={sponsoredTitleEnabled} />

        <div
          className={classNames(
            styles.attrs,
            styles.attrs__3col,
            styles.visible__3col
          )}
        >
          {showKeySpecifications && (
            <>
              {showHeroAttributes && (
                <HeroAttributes heroAttributes={heroAttributes} />
              )}
              {showPromoMessages && (
                <PromoMessages
                  brand={brand}
                  handleDisplayModal={handleDisplayPromoModal}
                  handleQuickViewClick={handlePromoMessagesClick}
                  hasPromoMessageModal={hasPromoMessageModal}
                  productUrl={productUrl}
                  promos={promoMessages}
                  title={title}
                />
              )}
              {showPreOrder && (
                <PreOrder
                  availableToOrder={
                    availability?.futureAvailability
                      ?.availableToOrderFromMessage
                  }
                  streetDate={availability?.preOrder?.streetDateMessage}
                />
              )}
              {showAdditionalMessages && (
                <AdditionalMessages messages={additionalMessages} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Price, Credit */}
      <div className={styles.thirdColumn}>
        <a href={productUrl} onClick={handleClick} tabIndex={0}>
          <Price
            brand={brand}
            creditMessage={creditMessage}
            dataTestIdPrefix="product-card"
            isProductCardDetailed
            price={price}
          />
          {hasProductRating && (
            <ProductCardRating className={styles.rating} rating={rating} />
          )}
        </a>

        {energy?.rating && (
          <div
            className={classNames(
              styles.energy,
              styles.energy__3col,
              styles.visible__3col
            )}
          >
            <EnergyRating
              className={styles.energyRating}
              handleEnergyRatingClick={handleEnergyArrowClick}
              hasEnergySideDrawer={hasEnergySideDrawer}
              rating={
                energy?.rating?.legacy ? undefined : energy?.rating?.value
              }
              ratingLegacy={
                energy?.rating?.legacy ? energy.rating.value : undefined
              }
            />
          </div>
        )}
      </div>

      {/* Energy */}
      {energy?.rating && (
        <div
          className={classNames(styles.energy, styles.hidden__3col, {
            [styles.energy__hasSwatches]: showSwatches,
          })}
        >
          <EnergyRating
            className={styles.energyRating}
            handleEnergyRatingClick={handleEnergyArrowClick}
            hasEnergySideDrawer={hasEnergySideDrawer}
            rating={energy?.rating?.legacy ? undefined : energy?.rating?.value}
            ratingLegacy={
              energy?.rating?.legacy ? energy.rating.value : undefined
            }
          />
        </div>
      )}

      {/* HeroAttributes, Promos, Additional */}
      <div className={classNames(styles.attrs, styles.hidden__3col)}>
        {showKeySpecifications && (
          <Accordion label="Key Specifications:" open={position === 1} size="S">
            {showHeroAttributes && (
              <HeroAttributes heroAttributes={heroAttributes} />
            )}
            {showPromoMessages && (
              <PromoMessages
                brand={brand}
                handleDisplayModal={handleDisplayPromoModal}
                handleQuickViewClick={handlePromoMessagesClick}
                hasPromoMessageModal={hasPromoMessageModal}
                productUrl={productUrl}
                promos={promoMessages}
                title={title}
              />
            )}
            {showPreOrder && (
              <PreOrder
                availableToOrder={
                  availability?.futureAvailability?.availableToOrderFromMessage
                }
                streetDate={availability?.preOrder?.streetDateMessage}
              />
            )}

            {showAdditionalMessages && (
              <AdditionalMessages messages={additionalMessages} />
            )}
          </Accordion>
        )}
      </div>

      <div
        className={classNames(styles.ctaRow, {
          [styles.ctaRow__noCompare]: !children,
        })}
      >
        {/* Compare */}
        {children && (
          <div className={styles.compare}>
            <div>{children}</div>
          </div>
        )}

        <CtaButton
          {...buttonSettings}
          className={styles.cta}
          emailConfirmed={emailConfirmed}
          handleMoreInformationClick={handleMoreInformationClick}
          handleQuickAddClick={handleQuickAddClick}
          handleQuickViewClick={handleQuickViewClick}
          product={product}
          productUrl={productUrl}
          selectedSwatch={selectedSwatch}
          swatchOrder={(colorSwatches || []).map(({ color }) => color)}
          swatchOverride={swatchOverride}
        />
      </div>
    </article>
  );
};

export default ProductCardListView;
